import { ReactComponent as BoschLogo } from "@/assets/ecu-brands/bosch.svg";
import { ReactComponent as ContinentalLogo } from "@/assets/ecu-brands/continental.svg";
import { ReactComponent as GetragLogo } from "@/assets/ecu-brands/getrag.svg";
import { ReactComponent as MarelliLogo } from "@/assets/ecu-brands/marelli.svg";
import { ReactComponent as TemicLogo } from "@/assets/ecu-brands/temic.svg";
import { ReactComponent as ZfLogo } from "@/assets/ecu-brands/zf.svg";
import { ReactComponent as AisinLogo } from "@/assets/ecu-brands/aisin.svg";
import { ReactComponent as DelcoLogo } from "@/assets/ecu-brands/acdelco.svg";
import { ReactComponent as DelphiLogo } from "@/assets/ecu-brands/delphi.svg";
import { ReactComponent as KeihinLogo } from "@/assets/ecu-brands/keihin.svg";
import { ReactComponent as VisteonLogo } from "@/assets/ecu-brands/visteon.svg";
import { ReactComponent as KeficoLogo } from "@/assets/ecu-brands/kefico.svg";
import densoLogoUrl from "@/assets/ecu-brands/denso.png";
import siemensLogoUrl from "@/assets/ecu-brands/siemens.png";
import hitachiLogoUrl from "@/assets/ecu-brands/hitachi.png";

// SVGs render as React components (crisp, tintable). Raster logos (PNG/JPG)
// render as plain <img> instead — same lookup, different element.
// Note: the scraped data calls this manufacturer "Delco" (not "ACDelco"),
// so the lookup key has to match the data, not the asset filename.
const LOGOS = {
  bosch: BoschLogo,
  continental: ContinentalLogo,
  getrag: GetragLogo,
  marelli: MarelliLogo,
  temic: TemicLogo,
  zf: ZfLogo,
  aisin: AisinLogo,
  delco: DelcoLogo,
  delphi: DelphiLogo,
  keihin: KeihinLogo,
  visteon: VisteonLogo,
  kefico: KeficoLogo,
};

const RASTER_LOGOS = {
  denso: densoLogoUrl,
  siemens: siemensLogoUrl,
  hitachi: hitachiLogoUrl,
};

const ALIASES = {
  acdelco: "delco",
  "ac delco": "delco",
  "magneti marelli": "marelli",
  "continental/siemens": "continental",
  "continental-siemens-vdo": "continental",
  "continental/siemens/vdo": "continental",
  "siemens/continental": "siemens",
  "conti temic": "temic",
};

function logoKey(name) {
  const raw = (name || "").trim().toLowerCase();
  return ALIASES[raw] || raw;
}

export function ecuBrandLogoAvailable(name) {
  const key = logoKey(name);
  return Boolean(LOGOS[key] || RASTER_LOGOS[key]);
}

// Pages that only have the combined "ecu_tcu.name" string (e.g. "Bosch
// MD1CS004") rather than a separate manufacturer field can pull the brand
// key out of the prefix instead. Returns null if no known brand matches.
export function ecuBrandFromEcuName(ecuName) {
  const lower = (ecuName || "").trim().toLowerCase();
  const known = [...Object.keys(LOGOS), ...Object.keys(RASTER_LOGOS)];
  return known.find((brand) => lower.startsWith(brand)) || null;
}

// ECU chip manufacturer logo (Bosch, Continental, ZF, ...) — distinct from
// the vehicle manufacturer_logo (BMW, VW, ...) already used elsewhere.
// Renders nothing if we don't have a logo asset for this manufacturer, so
// callers can fall back to plain text.
export default function EcuBrandLogo({ name, className = "h-4 w-auto" }) {
  const key = logoKey(name);
  const Logo = LOGOS[key];
  if (Logo) return <Logo className={className} title={name} aria-label={name} />;
  const rasterUrl = RASTER_LOGOS[key];
  if (rasterUrl) {
    return <img src={rasterUrl} alt={name} title={name} className={`${className} object-contain`} />;
  }
  return null;
}
